import { useState, useEffect } from 'react';
import { PRESET_TICKERS, TICKERS_STORAGE_KEY } from '../utils/tickers';
import { getValidToken } from '../utils/auth';
import ChartWidget from '../components/ChartWidget';
import CapSeriesChart from '../components/CapSeriesChart';
import SectorSeriesChart from '../components/SectorSeriesChart';
import ContractChartWidget from '../components/ContractChartWidget';
import GenericOptionsWidget from '../components/GenericOptionsWidget';
// import BestContractsWidget from '../components/BestContractsWidget'; // DISABLED 2026-07-10 (SPX Best Cheap Contracts paused)
import SmartEntryWidget from '../components/SmartEntryWidget';
import NewsFeed from '../components/NewsFeed';
// import ContractStatsHistory from '../components/ContractStatsHistory'; // DISABLED 2026-07-10

const API_URL = process.env.REACT_APP_API_URL || '';

export default function Options() {
  // Shared timeframe for SPY/stock chart + Cap Series chart
  const [tf, setTf] = useState('1m');

  // Symbol shown in the main candlestick chart (dropdown inside ChartWidget)
  const [chartSymbol, setChartSymbol] = useState('SPX');

  // Custom tickers — shared between ChartWidget dropdown and GenericOptionsWidget.
  // Stored server-side (table custom_tickers) so the list is the same on
  // every device/browser - this used to live in localStorage only, so a
  // ticker added on mobile was invisible on desktop and vice versa.
  const [customTickers, setCustomTickers] = useState([]);

  // One-time load: pull the server list, and push up anything still sitting
  // in this browser's old localStorage copy that the server doesn't know
  // about yet (so nothing added before this change gets silently dropped).
  useEffect(() => {
    let mounted = true;
    (async () => {
      let legacy = [];
      try { legacy = JSON.parse(localStorage.getItem(TICKERS_STORAGE_KEY)) || []; }
      catch { legacy = []; }
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/custom-tickers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        let tickers = data.tickers || [];
        const missing = legacy.filter(t => !tickers.includes(t));
        for (const t of missing) {
          const addRes = await fetch(`${API_URL}/api/custom-tickers/add`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ symbol: t }),
          });
          if (addRes.ok) tickers = (await addRes.json()).tickers || tickers;
        }
        if (mounted) setCustomTickers(tickers);
      } catch {
        if (mounted) setCustomTickers(legacy); // offline fallback
      }
    })();
    return () => { mounted = false; };
  }, []);

  const allTickers = [...PRESET_TICKERS, ...customTickers.filter(t => !PRESET_TICKERS.includes(t))];

  const handleCustomTickersChange = async (updated) => {
    setCustomTickers(updated); // optimistic
    const added = updated.find(t => !customTickers.includes(t));
    if (!added) return;
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/custom-tickers/add`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: added }),
      });
      if (res.ok) setCustomTickers((await res.json()).tickers || updated);
    } catch {
      // stays as the optimistic local update; will reconcile next page load
    }
  };

  // Selected contract for the contract chart (set by GenericOptionsWidget)
  const [selectedContract, setSelectedContract] = useState(null);

  return (
    <div>
      <ChartWidget
        tf={tf} setTf={setTf}
        symbol={chartSymbol} setSymbol={setChartSymbol}
        tickers={allTickers}
      />
      <CapSeriesChart tf={tf} setTf={setTf} />
      <SectorSeriesChart tf={tf} setTf={setTf} />
      <ContractChartWidget contract={selectedContract} />
      <GenericOptionsWidget
        onContractSelect={setSelectedContract}
        customTickers={customTickers}
        onCustomTickersChange={handleCustomTickersChange}
      />
      <NewsFeed />
      <SmartEntryWidget
        apiPath="/api/smart-signals-spy"
        title="Smart Entry (SPY)"
        subtitle="Same engine as Smart Entry (SPX), trading SPY contracts instead - $0.50 minimum bid (vs $0.05 for SPX) since SPY premiums run ~10x cheaper for the same delta - TS raises to 20% above entry once a signal gains 30% - Telegram alert on every fire"
      />
      <SmartEntryWidget
        apiPath="/api/smart-signals-spy-reversal"
        title="Smart Entry (SPY Reversal)"
        subtitle="Same gates as Smart Entry (SPY), but entry alignment uses a 45-minute trend composite instead of the standard 3-hour one, so it can catch an early reversal on the opposite side while the plain SPY variant is still holding its own position - experimental, unvalidated by a real-data backtest yet, tracked separately to judge on its own record"
      />
      <SmartEntryWidget
        apiPath="/api/smart-signals-nowait"
        title="Smart Entry (No-Wait + Trail Stop)"
        subtitle="Same as Smart Entry, but doesn't wait for an open signal to resolve before firing a new one (up to 5 concurrent per side) - TS raises to 20% above entry once a signal gains 30% from entry - Telegram alert on every fire"
      />
      <SmartEntryWidget />
      <SmartEntryWidget
        apiPath="/api/smart-signals-v2"
        title="Smart Entry + Delta Check (SPX)"
        subtitle="Same as Smart Entry, plus a delta-consistency gate: the contract's realized price move since watch-entry must track what its delta predicts from the SPY move over the same window"
      />
      {/* DISABLED 2026-07-10 (SPX Best Cheap Contracts paused) */}
      {/* <BestContractsWidget onContractSelect={setSelectedContract} /> */}
      {/* <ContractStatsHistory /> */}
    </div>
  );
}
